<template>
  <div class="chat-assistant-root" :class="{ 'chat-assistant-root--embedded': mode === 'embedded' }">
    <!-- Launcher mode (wizard page): opens the global panel -->
    <template v-if="mode === 'fab'">
      <transition name="chat-fade">
        <div v-if="!chat.isOpen" class="chat-fab" @click="open">
          <i class="tim-icons icon-chat-33"></i>
        </div>
      </transition>
    </template>

    <!-- Global mode: standard drawer, mounted once in DashboardLayout and
         opened from the navbar icon -->
    <template v-else-if="mode === 'global'">
      <base-drawer class="biku-drawer" :show="chat.isOpen" title="Biku Bot" width="440px" @close="close">
        <template #title-prefix>
          <span class="biku-drawer-avatar"><img src="/img/pandas/biku-bot.png" alt="" /></span>
        </template>
        <template #header-actions>
          <el-tooltip content="New chat" effect="light" :open-delay="300" placement="bottom">
            <button type="button" class="base-drawer__header-btn" :disabled="chat.isLoading" aria-label="New chat" @click="requestNewChat">
              <i class="tim-icons icon-simple-add"></i>
            </button>
          </el-tooltip>
        </template>

        <!-- Messages (the drawer body is the scroll container) -->
        <div class="chat-messages chat-messages--drawer" ref="messagesContainer">
          <div class="message message--assistant">
            <div class="message-content">
              Hi! I'm Biku Bot. Ask me anything about running your venue on Off The Couch.
            </div>
          </div>
          <div v-for="(msg, i) in chat.messages" :key="'g' + i"
               class="message" :class="'message--' + msg.role">
            <div class="message-content" v-html="formatMessage(msg.content)"></div>
            <div v-if="msg.reportable" class="message-feedback">
              <template v-if="!msg.reported">
                <button class="feedback-btn" :disabled="msg.reporting" @click="reportMessage(i, 'wrong_answer')">Wrong answer</button>
                <button class="feedback-btn" :disabled="msg.reporting" @click="reportMessage(i, 'not_helpful')">Not helpful</button>
              </template>
              <span v-else class="feedback-reported">
                <i class="tim-icons icon-check-2"></i> Reported, thanks
              </span>
            </div>
          </div>
          <div v-if="chat.isLoading" class="message message--assistant">
            <div class="message-content">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
          <div v-if="chat.rateLimit" class="rate-limit-notice">
            <i class="tim-icons icon-time-alarm mr-1"></i>
            {{ rateLimitText }}
          </div>
        </div>

        <template #footer>
          <div class="chat-input chat-input--drawer">
            <textarea
              v-model="inputText"
              @keydown="handleKeydown"
              @input="autoResize"
              placeholder="Type your question..."
              :disabled="chat.isLoading || !!chat.rateLimit"
              rows="1"
              maxlength="500"
            ></textarea>
            <button
              @click="handleSend"
              :disabled="!inputText.trim() || chat.isLoading || !!chat.rateLimit"
              class="send-btn"
              aria-label="Send message">
              <i class="tim-icons icon-send"></i>
            </button>
          </div>
        </template>
      </base-drawer>
    </template>

    <!-- Embedded mode (onboarding side panel Assistant tab) -->
    <div v-else class="chat-panel chat-panel--embedded">
      <!-- Messages -->
      <div class="chat-messages" ref="messagesContainer">
        <div class="message message--assistant">
          <div class="message-content">
            Hi! I'm Biku Bot. Ask me anything about running your venue on Off The Couch.
          </div>
        </div>
        <div v-for="(msg, i) in chat.messages" :key="'e' + i"
             class="message" :class="'message--' + msg.role">
          <div class="message-content" v-html="formatMessage(msg.content)"></div>
          <div v-if="msg.reportable" class="message-feedback">
            <template v-if="!msg.reported">
              <button class="feedback-btn" :disabled="msg.reporting" @click="reportMessage(i, 'wrong_answer')">Wrong answer</button>
              <button class="feedback-btn" :disabled="msg.reporting" @click="reportMessage(i, 'not_helpful')">Not helpful</button>
            </template>
            <span v-else class="feedback-reported">
              <i class="tim-icons icon-check-2"></i> Reported, thanks
            </span>
          </div>
        </div>
        <div v-if="chat.isLoading" class="message message--assistant">
          <div class="message-content">
            <div class="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
        <div v-if="chat.rateLimit" class="rate-limit-notice">
          <i class="tim-icons icon-time-alarm mr-1"></i>
          {{ rateLimitText }}
        </div>
      </div>

      <!-- Input -->
      <div class="chat-input">
        <textarea
          v-model="inputText"
          @keydown="handleKeydown"
          @input="autoResize"
          placeholder="Type your question..."
          :disabled="chat.isLoading || !!chat.rateLimit"
          rows="1"
          maxlength="500"
        ></textarea>
        <button
          @click="handleSend"
          :disabled="!inputText.trim() || chat.isLoading || !!chat.rateLimit"
          class="send-btn"
          aria-label="Send message">
          <i class="tim-icons icon-send"></i>
        </button>
      </div>
    </div>

    <base-alert-dialog
      :show="confirmNewChat"
      title="Start a new chat?"
      message="This clears the current conversation from the chat panel."
      variant="warning"
      show-cancel
      confirm-text="New chat"
      cancel-text="Cancel"
      @confirm="confirmNew"
      @cancel="confirmNewChat = false" />
  </div>
</template>

<script>
import {
  useBikuChat,
  openBikuChat,
  closeBikuChat,
  startNewBikuChat,
  sendBikuMessage,
  reportBikuMessage
} from '@/composables/useBikuChat';


// One conversation, three surfaces: 'fab' is the wizard page launcher, 'global'
// is the panel mounted once in DashboardLayout (opened from the navbar), and
// 'embedded' is the onboarding side panel's Assistant tab. All state lives in
// useBikuChat so it survives route changes, tab switches and refreshes.
export default {
  name: 'ChatAssistant',
  props: {
    mode: {
      type: String,
      default: 'fab',
      validator: v => ['fab', 'global', 'embedded'].includes(v)
    },
    categoryId: { type: [Number, String], default: null },
    taskId: { type: [Number, String], default: null },
    currentTask: { type: Object, default: null }
  },
  data() {
    return {
      chat: useBikuChat(),
      inputText: '',
      confirmNewChat: false
    };
  },
  computed: {
    rateLimitText() {
      return this.chat.rateLimit === 'user_daily_limit'
        ? "You've reached your daily Biku chat limit. Try again tomorrow."
        : "Your team has reached today's Biku chat limit. Try again tomorrow.";
    }
  },
  watch: {
    'chat.messages.length'() {
      this.scrollToBottom();
    },
    'chat.isLoading'() {
      this.scrollToBottom();
    },
    'chat.isOpen'(open) {
      if (open) this.scrollToBottom();
    }
  },
  mounted() {
    this.scrollToBottom();
    // BaseDrawer does not handle ESC; the global drawer adds its own listener.
    if (this.mode === 'global') {
      document.addEventListener('keydown', this.onEscape);
    }
  },
  beforeDestroy() {
    if (this.mode === 'global') {
      document.removeEventListener('keydown', this.onEscape);
    }
  },
  methods: {
    onEscape(e) {
      if (e.key !== 'Escape' || !this.chat.isOpen || this.confirmNewChat) return;
      this.close();
    },

    context() {
      return { routePath: this.$route.path, taskId: this.taskId, categoryId: this.categoryId };
    },

    open() {
      openBikuChat();
    },

    close() {
      closeBikuChat();
    },

    requestNewChat() {
      if (this.chat.messages.length === 0) return;
      this.confirmNewChat = true;
    },

    confirmNew() {
      startNewBikuChat();
      this.confirmNewChat = false;
    },

    formatMessage(content) {
      if (!content) return '';
      // HTML encode first to prevent XSS
      let formatted = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Bold
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Inline code
      formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
      // Bullet lists
      formatted = formatted.replace(/^[-•]\s+(.+)$/gm, '<li>$1</li>');
      formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
      // Numbered lists
      formatted = formatted.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');
      // Newlines
      formatted = formatted.replace(/\n/g, '<br>');
      // Clean up br inside lists
      formatted = formatted.replace(/<br><ul>/g, '<ul>');
      formatted = formatted.replace(/<\/ul><br>/g, '</ul>');
      formatted = formatted.replace(/<br><li>/g, '<li>');
      return formatted;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer;
        if (!el) return;
        // In the drawer the BaseDrawer body scrolls, not the message list.
        const scroller = this.mode === 'global' ? el.closest('.base-drawer__body') : el;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },

    handleKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    },

    autoResize(e) {
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 80) + 'px';
    },

    async handleSend() {
      const text = this.inputText.trim();
      if (!text || this.chat.isLoading || this.chat.rateLimit) return;
      this.inputText = '';
      const status = await sendBikuMessage(text, this.context());
      if (status === 'unauthorized') {
        this.$router.push('/forbidden');
      }
    },

    async reportMessage(index, rating) {
      const status = await reportBikuMessage(index, rating, this.context());
      if (status === 'limited' || status === 'error') {
        this.$notify({
          type: 'danger',
          icon: 'tim-icons icon-alert-circle-exc',
          message: status === 'limited'
            ? 'Report limit reached for today. Please try again tomorrow.'
            : 'Could not send the report. Please try again.'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* FAB button */
.chat-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e14eca, #ba54f5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(225, 78, 202, 0.45);
  z-index: 1050;
  transition: transform 0.2s ease;
  border: none;

  &:hover {
    transform: scale(1.08);
  }
}

/* Chat panel - shared */
.chat-panel {
  display: flex;
  flex-direction: column;
}

/* Global (drawer) mode: BaseDrawer's body scrolls and its footer pads, so the
   message list and input drop their own scroll and chrome. Doubled classes so
   these win over .chat-messages / .chat-input, which are defined further down. */
.chat-messages.chat-messages--drawer {
  flex: none;
  overflow: visible;
  padding: 0;
}

.chat-input.chat-input--drawer {
  /* One composer field: textarea and send button share a single border. */
  padding: 5px 5px 5px 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  align-items: flex-end;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #e14eca;
  }

  textarea {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 7px 0;

    &:focus {
      border: none;
    }

    &:disabled {
      background: transparent;
    }
  }

  .send-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;

    &:disabled {
      background: rgba(125, 125, 125, 0.25);
      opacity: 1;
    }
  }
}

/* Compact header for the chat drawer only: the 48px avatar already sets its
   height, so BaseDrawer's standard 24px vertical padding reads as too much. */
.biku-drawer ::v-deep .base-drawer__header {
  padding: 18px 20px 12px 16px;
}

.biku-drawer ::v-deep .base-drawer__header-top {
  align-items: center;
}

/* biku-bot.png is 400x400 but the artwork spans only x 10-89% and y 16-69%
   (measured). An 82px image makes the artwork ~65px wide (headphones included)
   by ~43px tall, so the box is 66px wide and 48px tall: full size, nothing
   cropped, header height unchanged. translateY recentres the artwork, which
   sits above the image's centre. Overflow stays visible: everything outside
   the box is transparent.
   max-width: none undoes the theme's global img { max-width: 100% }. Spacing
   to the title comes from BaseDrawer's title-wrap gap. */
.biku-drawer-avatar {
  width: 66px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 82px;
    height: 82px;
    max-width: none;
    flex-shrink: 0;
    border-radius: 0;
    transform: translateY(6px);
  }
}

/* Embedded-mode root: become a flex column inside .drawer-body.no-padding so the
   chat panel can claim its share via flex: 1 and the messages list can shrink. */
.chat-assistant-root--embedded {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.chat-panel--embedded {
  flex: 1;
  min-height: 0;
  border: none;
  background: transparent;
}

/* Messages area */
.chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

.message {
  margin-bottom: 12px;
  max-width: 85%;
}

.message--user {
  margin-left: auto;

  .message-content {
    background: rgba(225, 78, 202, 0.15);
    border: 1px solid rgba(225, 78, 202, 0.25);
    border-radius: 12px 12px 2px 12px;
    padding: 10px 14px;
    color: rgba(255, 255, 255, 0.95);
    font-size: 13px;
    line-height: 1.5;
  }
}

.message--assistant {
  margin-right: auto;

  .message-content {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px 12px 12px 2px;
    padding: 10px 14px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    line-height: 1.5;

    ::v-deep strong {
      color: rgba(255, 255, 255, 0.95);
    }

    ::v-deep code {
      background: rgba(255, 255, 255, 0.08);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 12px;
    }

    ::v-deep ul {
      margin: 6px 0;
      padding-left: 18px;
    }

    ::v-deep li {
      margin-bottom: 4px;
    }
  }
}

/* Per-message feedback actions */
.message-feedback {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 3px 4px 0;
}

.feedback-btn {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #e14eca;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.feedback-reported {
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);

  i {
    font-size: 9px;
  }
}

/* Typing indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    animation: typing-bounce 1.4s infinite ease-in-out;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing-bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-6px); }
}

/* Rate limit notice */
.rate-limit-notice {
  text-align: center;
  padding: 10px;
  color: rgba(251, 191, 36, 0.8);
  font-size: 12px;
  background: rgba(251, 191, 36, 0.06);
  border-radius: 8px;
  margin-top: 8px;
}

/* Input area */
.chat-input {
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-shrink: 0;

  textarea {
    flex: 1;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 8px 12px;
    color: white;
    font-size: 13px;
    font-family: inherit;
    resize: none;
    max-height: 80px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    &:focus {
      border-color: #e14eca;
      outline: none;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .send-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #e14eca;
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: opacity 0.2s ease;

    &:hover:not(:disabled) {
      opacity: 0.85;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

.chat-fade-enter-active,
.chat-fade-leave-active {
  transition: opacity 0.2s ease;
}

.chat-fade-enter,
.chat-fade-leave-to {
  opacity: 0;
}

/* Mobile — FAB is bigger than desktop (thumb-friendly target) and sits higher above the safe-area inset */
@media (max-width: 768px) {
  .chat-fab {
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    right: 20px;
    width: 72px;
    height: 72px;
    font-size: 32px;
    box-shadow: 0 8px 28px rgba(225, 78, 202, 0.55);
  }
}
</style>

<style lang="scss">
/* Light mode overrides */
.white-content {
  .chat-fab {
    box-shadow: 0 4px 20px rgba(225, 78, 202, 0.25);
  }

  .chat-messages {
    &::-webkit-scrollbar-track { background: #f5f6fa; }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      &:hover { background: rgba(0, 0, 0, 0.15); }
    }
  }

  .message--user .message-content {
    background: rgba(225, 78, 202, 0.08);
    border-color: rgba(225, 78, 202, 0.2);
    color: #344767;
  }

  .message--assistant .message-content {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.08);
    color: #344767;

    strong { color: #1a1a2e; }
    code { background: rgba(0, 0, 0, 0.06); }
  }

  .chat-input {
    border-top-color: #e3e3e3;

    textarea {
      background: #f8f9fa;
      border-color: #e3e3e3;
      color: #344767;

      &:focus { border-color: #e14eca; }
      &::placeholder { color: rgba(0, 0, 0, 0.3); }
    }
  }

  /* Doubled class to beat the scoped dark composer rule. */
  .chat-input.chat-input--drawer {
    background: #ffffff;
    border-color: #e3e3e3;

    &:focus-within { border-color: #e14eca; }

    textarea { background: transparent; }
  }

  .rate-limit-notice {
    background: rgba(251, 191, 36, 0.08);
    color: #b45309;
  }
}
</style>
